import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import { useAuthStore } from '../stores/auth'
import { app } from '../main'
import { useToast } from 'vuestic-ui'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'preferences' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    meta: { requireAuth: true },
    redirect: { name: 'preferences' },
    children: [
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
        meta: { requireRoles: ['ADMIN'] },
      },
      {
        name: 'files',
        path: 'files',
        component: () => import('../pages/files/FIlesPage.vue'),
      },
      {
        name: 'mandatory',
        path: 'mandatory',
        component: () => import('../pages/mandatory/MandatoryDisplayPage.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: '2fa',
        path: 'two-factor',
        component: () => import('../pages/auth/TwoFactor.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        name: 'logout',
        path: 'logout',
        component: () => null,
        beforeEnter: async () => {
          const response = await app.config.globalProperties.$vaModal.confirm({
            title: 'Se déconnecter',
            message: `Êtes-vous sûr de vouloir vous déconnecter?`,
            okText: 'Se déconnecter',
            size: 'small',
            maxWidth: '380px',
          })

          if (!response) {
            return false
          }

          const authStore = useAuthStore()
          authStore.logout()
          return { name: 'login' }
        },
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to) => {
  // redirect to login page if not logged in and trying to access a restricted page
  const authStore = useAuthStore()
  if ((to.meta?.requireAuth || to.name === 'login') && authStore.require2FA) {
    return { name: '2fa' }
  }
  if ((to.meta?.requireAuth && !authStore.isAuthenticated) || (to.name === '2fa' && authStore.needLogin)) {
    return { name: 'login' }
  }
  if ((to.name === 'login' || to.name === '2fa') && authStore.isAuthenticated) {
    return { name: 'admin' }
  }
  if (authStore.userProfile === null && authStore.access_token) {
    await authStore.getUserProfile() // Fetch user profile on each route change
  }
  if (
    (to.name === 'files' || to.name === 'mandatory' || to.name === 'settings') &&
    (!authStore.isActiveUser || authStore.isPendingUser)
  ) {
    const { init: notify } = useToast()
    notify({
      message: 'Votre compte doit être validé et avec votre adresse email confirmé pour accéder à cette section',
      color: 'danger',
    })
    return { name: 'preferences' }
  }

  if (to.meta?.requireRoles && !(to.meta.requireRoles as string[]).includes(authStore.userProfile?.role as string)) {
    return { name: 'admin' }
  }
})

export default router
